import * as Yup from 'yup';
import {
  COLOR_ERROR_MESSAGE,
  FIELD_REQUIRED_MESSAGE,
  IMAGE_REQUIRED_MESSAGE,
  MAX_BANNERS_COUNT,
  SECTIONS_REQUIRED_MESSAGE,
  UrlValidator,
  colorRegex,
} from '../constants';
import { OnboardingJourney, PatientCommunicationChannel } from '../types/websiteConfiguration';

const prescriberValidationFields = {
  private_prescriptions_journey: Yup.object().shape({
    collection: Yup.boolean().optional(),
    delivery: Yup.boolean().optional(),
    classic_mode_collection: Yup.boolean().optional(),
  }).test(
    'deliveryOptionsTest',
    'At least one delivery option must be selected',
    (obj) => {
      if (obj?.collection || obj?.delivery) {
        return true;
      }

      return false;
    }
  ).nullable(),
  communication_channel: Yup.string()
    .oneOf(Object.values(PatientCommunicationChannel))
    .required('Please select patient communication channel'),
};

export const getValidationSchema = (isPrescriber?: boolean) =>
  Yup.object().shape({
    enabled: Yup.boolean().optional(),
    logo: Yup.string(),
    logo_white: Yup.string(),
    logo_url: Yup.string(),
    logo_url_white: Yup.string(),
    favicon: Yup.object().shape({
      '16x16': Yup.string().optional(),
      '32x32': Yup.string().optional(),
      '48x48': Yup.string().optional(),
      '192x192': Yup.string().optional(),
    }),
    hero: Yup.object()
      .shape({
        headline: Yup.string().optional(),
        subtitle: Yup.string().optional(),
        images: Yup.array(Yup.string().nullable()).optional(),
        image_urls: Yup.array(Yup.string()).optional(),
        learn_more_intro: Yup.string().optional(),
        cta_text: Yup.string().optional(),
        cta_link: Yup.string().optional(),
      })
      .optional(),
    branding: Yup.object()
      .shape({
        colors: Yup.object()
          .shape({
            primary: Yup.string()
              .matches(colorRegex, COLOR_ERROR_MESSAGE)
              .optional(),
            light_primary: Yup.string()
              .matches(colorRegex, COLOR_ERROR_MESSAGE)
              .optional(),
            secondary: Yup.string()
              .matches(colorRegex, COLOR_ERROR_MESSAGE)
              .optional(),
            tertiary: Yup.string()
              .matches(colorRegex, COLOR_ERROR_MESSAGE)
              .optional(),
          })
          .required(),
      })
      .required(),
    banners: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.number()
            .integer()
            .positive()
            .required(),
          title: Yup.string().optional(),
          link: Yup.string().optional(),
          image: Yup.string().when('image_url', {
            is: (image_url) => !image_url,
            then: Yup.string().required(IMAGE_REQUIRED_MESSAGE),
          }),
          image_url: Yup.string(),
        })
      )
      .optional()
      .max(MAX_BANNERS_COUNT),
    domain: UrlValidator.optional(),
    homepage_url: UrlValidator.optional(),
    homepage_sections: Yup.array()
      .of(Yup.string())
      .optional()
      .min(1, SECTIONS_REQUIRED_MESSAGE),
    about_us: Yup.object()
      .shape({
        enabled: Yup.boolean().optional(),
        title: Yup.string().when('enabled', { is: true, then: Yup.string().required(FIELD_REQUIRED_MESSAGE) }),
        description: Yup.string().when('enabled', { is: true, then: Yup.string().required(FIELD_REQUIRED_MESSAGE) }),
        image: Yup.string().when('enabled', {
          is: true,
          then: Yup.string().when('image_url', {
            is: (image_url) => !image_url,
            then: Yup.string().required(IMAGE_REQUIRED_MESSAGE),
          }),
        }),
        image_url: Yup.string(),
      })
      .required(),
    contact_us: Yup.object()
      .shape({
        enabled: Yup.boolean(),
        contact_name: Yup.string().when('enabled', { is: true, then: Yup.string().required(FIELD_REQUIRED_MESSAGE) }),
        contact_address: Yup.string().when('enabled', {
          is: true,
          then: Yup.string().required(FIELD_REQUIRED_MESSAGE),
        }),
        email: Yup.string().when('enabled', { is: true, then: Yup.string().required(FIELD_REQUIRED_MESSAGE) }),
        phone_number: Yup.string().when('enabled', { is: true, then: Yup.string().required(FIELD_REQUIRED_MESSAGE) }),
        gphc_number: Yup.string().optional(),
        gphc_url: Yup.string().optional(),
        display_gphc_logo: Yup.boolean().optional(),
        pharmacy_superintendent_name: Yup.string().optional(),
        pharmacy_superintendent_registration_number: Yup.string().optional(),
        pharmacy_superintendent_gphc_url: Yup.string().optional(),
      })
      .required(),
    footer_custom_links: Yup.array()
      .of(
        Yup.object().shape({
          title: Yup.string().required(FIELD_REQUIRED_MESSAGE),
          link: Yup.string().required(FIELD_REQUIRED_MESSAGE),
        })
      )
      .optional()
      .min(1),
    meta: Yup.object()
      .shape({
        title: Yup.string(),
        description: Yup.string(),
        'og:title': Yup.string(),
        'og:description': Yup.string(),
        image: Yup.string().optional(),
        'og:image': Yup.string().optional(),
      })
      .optional(),
    onboarding_journey: Yup.string()
      .oneOf(Object.values(OnboardingJourney))
      .optional(),
    ...(isPrescriber && prescriberValidationFields),
    pharmacy_required: Yup.boolean(),
    pharmacy_required_redirect: Yup
      .string()
      .when('pharmacy_required', {
        is: true,
        then: Yup.string().required('Please provide "Pharmacy required redirect url"'),
      }),
  });
